import React, { useState, useEffect, useMemo, useCallback, Suspense, lazy } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NumberBall } from "@/components/NumberBall";
import { SEOHead } from "@/components/SEOHead";
import { Calendar, Target, TrendingUp, Flame, Snowflake, Clock, Award } from "lucide-react";
import { LotterySubpagesLinks, RelatedLotteriesLinks, BreadcrumbNav } from "@/components/InternalLinksSEO";
import { LotteryKPIDisplay } from "@/components/LotteryKPIDisplay";

import { API } from "@/config/api";

// Lazy load heavy components
const WinningOdds = lazy(() => import("@/components/WinningOdds"));

// Skeleton component for loading states
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-700/50 rounded ${className}`} />
);

const LaPrimitivaPage = () => {
  const { lang = 'en' } = useParams();

  // Split state by priority - critical data loads first
  const [latestDraw, setLatestDraw] = useState(null);
  const [nextDraw, setNextDraw] = useState(null);
  const [criticalLoading, setCriticalLoading] = useState(true);
  
  // Secondary data loads after initial render
  const [predictions, setPredictions] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [recentDraws, setRecentDraws] = useState([]);
  const [kpiV2Data, setKpiV2Data] = useState(null);
  const [secondaryLoading, setSecondaryLoading] = useState(true);

  // Guards to prevent duplicate fetches
  const criticalFetchedRef = React.useRef(false);

  // Memoized date formatter
  const safeFormatDate = useCallback((rawDate) => {
    if (!rawDate) return "N/A";
    const d = new Date(rawDate);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  // STEP 1: Load critical data ONLY (latest + next draw) - fast
  useEffect(() => {
    if (criticalFetchedRef.current) return;
    criticalFetchedRef.current = true;
    const loadCritical = async () => {
      try {
        const [latestRes, nextRes] = await Promise.all([
          axios.get(`${API}/lottery/laprimitiva/latest`),
          axios.get(`${API}/lottery/laprimitiva/next`),
        ]);
        setLatestDraw(latestRes.data);
        setNextDraw(nextRes.data);
      } catch (e) {
        console.error("LaPrimitiva critical load error:", e);
      } finally {
        setCriticalLoading(false);
      }
    };
    loadCritical();
  }, []);

  // STEP 2: Load secondary data AFTER first render (deferred)
  useEffect(() => {
    if (criticalLoading) return;
    let cancelled = false;

    const loadSecondary = async () => {
        try {
          const results = await Promise.allSettled([
            axios.get(`${API}/lottery/laprimitiva/predictions`),
            axios.get(`${API}/lotteries/laprimitiva/stats?window=50`),
            axios.get(`${API}/lotteries/laprimitiva/draws?limit=10`),
            axios.get(`${API}/kpi-v2/laprimitiva`),
          ]);

          const [preds, stats, draws, kpiV2] = results.map((r) =>
            r.status === "fulfilled" ? r.value.data : null
          );

          setPredictions(preds);
          setStatistics(stats);
          setRecentDraws(draws?.draws || []);
          setKpiV2Data(kpiV2);
        } catch (e) {
          console.error("LaPrimitiva secondary load error:", e);
        } finally {
          if (!cancelled) setSecondaryLoading(false);
        }
      };
      loadSecondary();

    return () => { cancelled = true; };
  }, [criticalLoading]);

  // Memoize derived data
  const hotNumbers = useMemo(() => statistics?.hot_numbers || [], [statistics]);
  const coldNumbers = useMemo(() => statistics?.cold_numbers || [], [statistics]);
  const overdueNumbers = useMemo(() => statistics?.overdue_numbers || [], [statistics]);

  const formatPI = useCallback((pi) => {
    if (pi === null || pi === undefined) return 'N/A';
    return `${pi.toFixed(2)}×`;
  }, []);

  // RENDER IMMEDIATELY - No blocking loading screen
  return (
    <div className="min-h-screen py-4">
      <SEOHead title="La Primitiva" path="/lottery/laprimitiva" />

      <div className="max-w-7xl mx-auto px-4">
        {/* BREADCRUMB NAV */}
        <BreadcrumbNav 
          items={[{ label: 'La Primitiva' }]} 
          className="mb-3"
        />
        
        {/* HEADER - Always renders instantly */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-white mb-1">La Primitiva 🇪🇸</h1>
          <p className="text-gray-400 text-sm">
            Draws: Monday, Thursday & Saturday | Jackpot starts at €3M
          </p>

          <div className="flex gap-2 mt-3">
            <Link to={`/${lang}/predictions?lottery=laprimitiva`}>
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                <Target className="w-3 h-3 mr-1" />
                Generate Prediction
              </Button>
            </Link>

            <Link to={`/${lang}/statistics?lottery=laprimitiva`}>
              <Button
                size="sm"
                variant="outline"
                className="border-gray-700 text-white hover:bg-gray-800"
              >
                <TrendingUp className="w-3 h-3 mr-1" />
                View Statistics
              </Button>
            </Link>
          </div>
          
          <LotterySubpagesLinks 
            lotteryCode="laprimitiva" 
            lotteryName="La Primitiva"
            className="mt-4"
          />
        </div>

        {/* MAIN LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3.5 mb-3">
          
          {/* LEFT SECTION */}
          <div className="lg:col-span-3 space-y-3">
            
            {/* TOP ROW: Latest Draw / Next Draw / Predictions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              
              {/* LATEST DRAW - Critical */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm">Latest Draw</CardTitle>
                  <span className="text-[10px] text-gray-400 block">
                    {criticalLoading ? <Skeleton className="w-16 h-3 inline-block" /> : safeFormatDate(latestDraw?.date)}
                  </span>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3">
                  {criticalLoading ? (
                    <div className="flex flex-wrap gap-1">
                      {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="w-6 h-6 rounded-full" />
                      ))}
                    </div>
                  ) : latestDraw?.main_numbers?.length ? (
                    <>
                      <div className="flex flex-wrap gap-1 mb-1">
                        {latestDraw.main_numbers.map((n, i) => (
                          <NumberBall key={i} number={n} variant="main" size="xs" />
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2 text-[10px] text-gray-400">
                        {latestDraw.complementario != null && (
                          <div className="flex items-center gap-1">
                            <span>C:</span>
                            <div className="w-5 h-5 flex items-center justify-center rounded-full text-white text-[10px] font-semibold" style={{backgroundColor: '#8EC8FF'}}>
                              {latestDraw.complementario}
                            </div>
                          </div>
                        )}
                        {latestDraw.reintegro != null && (
                          <div className="flex items-center gap-1">
                            <span>R:</span>
                            <div className="w-5 h-5 flex items-center justify-center rounded-full text-white text-[10px] font-semibold" style={{backgroundColor: '#3478F6'}}>
                              {latestDraw.reintegro}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-400 text-xs">No recent draws</p>
                  )}
                </CardContent>
              </Card>

              {/* NEXT DRAW - Critical */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Next Draw
                  </CardTitle>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3 text-white">
                  {criticalLoading ? (
                    <Skeleton className="w-24 h-5" />
                  ) : (
                    <>
                      <p className="text-base font-bold">
                        {safeFormatDate(nextDraw?.next_draw_date)}
                      </p>
                      {nextDraw?.next_jackpot && (
                        <span className="text-[10px] text-gray-400 block">
                          €{nextDraw.next_jackpot.toLocaleString()}
                        </span>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* PREDICTIONS - Secondary */}
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm">Predictions</CardTitle>
                </CardHeader>

                <CardContent className="pt-0 pb-2 px-3">
                  {secondaryLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="w-full h-4" />
                      <Skeleton className="w-3/4 h-4" />
                    </div>
                  ) : predictions?.current_predictions?.length > 0 ? (
                    <div className="space-y-1.5">
                      <div>
                        <h4 className="text-[9px] font-semibold text-blue-400 mb-0.5">📊 Statistical</h4>
                        {(() => {
                          const p = predictions.current_predictions.find((x) => x.model === "statistical");
                          if (!p) return null;
                          return (
                            <div className="flex flex-wrap gap-0.5 items-center">
                              {p.main_numbers?.slice(0, 6).map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                              {(p.reintegro != null || p.bonus_numbers?.[0] != null) && (
                                <div className="flex items-center gap-0.5 ml-1">
                                  <span className="text-[8px] text-gray-500">R:</span>
                                  <div className="w-4 h-4 flex items-center justify-center rounded-full text-white text-[9px] font-semibold" style={{backgroundColor: '#3478F6'}}>
                                    {p.reintegro ?? p.bonus_numbers[0]}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div>
                        <h4 className="text-[9px] font-semibold text-amber-400 mb-0.5 flex items-center gap-1">
                          🔥 Lotterics
                          <span className="text-[8px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">New Lotterics Engine (V5)</span>
                        </h4>
                        {(() => {
                          const p = predictions.current_predictions.find((x) => x.model === "lotteryx");
                          if (!p) return null;
                          return (
                            <div className="flex flex-wrap gap-0.5 items-center">
                              {p.main_numbers?.slice(0, 6).map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                              {(p.reintegro != null || p.bonus_numbers?.[0] != null) && (
                                <div className="flex items-center gap-0.5 ml-1">
                                  <span className="text-[8px] text-gray-500">R:</span>
                                  <div className="w-4 h-4 flex items-center justify-center rounded-full text-white text-[9px] font-semibold" style={{backgroundColor: '#3478F6'}}>
                                    {p.reintegro ?? p.bonus_numbers[0]}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-xs">No predictions available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* SECOND ROW: Hot / Cold / Overdue */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              
              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Flame className="w-4 h-4 text-red-400" />
                    Hot Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Most frequent in last 50 draws</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {secondaryLoading ? (
                    <div className="flex flex-wrap gap-1">
                      {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="w-6 h-6 rounded-full" />
                      ))}
                    </div>
                  ) : hotNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {hotNumbers.slice(0, 6).map((n, i) => (
                        <NumberBall key={i} number={n} variant="hot" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Snowflake className="w-4 h-4 text-blue-400" />
                    Cold Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Least frequent in last 50 draws</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {secondaryLoading ? (
                    <div className="flex flex-wrap gap-1">
                      {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="w-6 h-6 rounded-full" />
                      ))}
                    </div>
                  ) : coldNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {coldNumbers.slice(0, 6).map((n, i) => (
                        <NumberBall key={i} number={n} variant="cold" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gray-900/40 border-gray-700/50">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-400" />
                    Overdue Numbers
                  </CardTitle>
                  <p className="text-[9px] text-gray-500 mt-0.5">Longest since last appearance</p>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-3">
                  {secondaryLoading ? (
                    <div className="flex flex-wrap gap-1">
                      {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="w-6 h-6 rounded-full" />
                      ))}
                    </div>
                  ) : overdueNumbers.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {overdueNumbers.slice(0, 6).map((item, i) => (
                        <NumberBall key={i} number={typeof item === 'object' ? item.number : item} variant="main" size="xs" />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No data</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* RECENT DRAWS */}
            <Card className="bg-gray-900/40 border-gray-700/50">
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-white text-sm">Recent 10 Draws</CardTitle>
              </CardHeader>

              <CardContent className="pt-0 pb-2 px-3 overflow-x-auto">
                {secondaryLoading ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="w-full h-6" />
                    ))}
                  </div>
                ) : recentDraws.length ? (
                  <table className="w-full text-xs">
                    <tbody>
                      {recentDraws.map((d, idx) => (
                        <tr key={d.id || idx} className="border-b border-gray-700/30 last:border-0">
                          <td className="py-1.5 text-gray-300 w-24">
                            {safeFormatDate(d.draw_date || d.date)}
                          </td>
                          <td className="py-1.5">
                            <div className="flex flex-wrap gap-1 items-center">
                              {d.main_numbers.map((n, i) => (
                                <NumberBall key={i} number={n} variant="main" size="xs" />
                              ))}
                              {d.complementario != null && (
                                <div className="flex items-center gap-0.5 ml-1">
                                  <span className="text-[9px] text-gray-500">C:</span>
                                  <div className="w-4 h-4 flex items-center justify-center rounded-full text-white text-[9px] font-semibold" style={{backgroundColor: '#8EC8FF'}}>
                                    {d.complementario}
                                  </div>
                                </div>
                              )}
                              {d.reintegro != null && (
                                <div className="flex items-center gap-0.5">
                                  <span className="text-[9px] text-gray-500">R:</span>
                                  <div className="w-4 h-4 flex items-center justify-center rounded-full text-white text-[9px] font-semibold" style={{backgroundColor: '#3478F6'}}>
                                    {d.reintegro}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-xs text-gray-400">No recent draws</p>
                )}
              </CardContent>
            </Card>
          </div>

                    {/* RIGHT SECTION */}
          <div className="space-y-3">
            {/* KPI v2 Display */}
            <LotteryKPIDisplay 
              kpiData={kpiV2Data} 
              loading={secondaryLoading}
              className="bg-gray-900/40 border-gray-700/50"
            />

            {/* ADVERTISEMENT */}{/* ADVERTISEMENT */}
            <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
              <div className="w-full h-24 bg-gradient-to-b from-amber-500/10 to-amber-600/5 rounded-lg flex items-center justify-center border border-amber-500/20">
                <div className="text-center">
                  <div className="text-amber-500/60 text-xs font-medium mb-0.5">
                    Advertisement
                  </div>
                  <div className="text-amber-500/40 text-[10px]">300 × 100</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* WINNING ODDS - Lazy loaded */}
        <Suspense fallback={<Skeleton className="w-full h-32 mt-4" />}>
          <WinningOdds lotteryCode="laprimitiva" />
        </Suspense>
        
        {/* RELATED LOTTERIES */}
        <div className="mt-8 pt-6 border-t border-gray-800">
          <RelatedLotteriesLinks 
            currentLotteryCode="laprimitiva"
            title="Explore Other Lotteries"
          />
        </div>
      </div>
    </div>
  );
};

export default LaPrimitivaPage;
